import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Clock, Calendar, ArrowDownUp, Clock2, History, Cpu } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов конвертера
import ConverterForm from './UnixTimeConverterPage/components/ConverterForm';
import ResultsDisplay from './UnixTimeConverterPage/components/ResultsDisplay';
import HistoryTable from './UnixTimeConverterPage/components/HistoryTable';
import TimezoneSelector from './UnixTimeConverterPage/components/TimezoneSelector';
import InfoContent from './UnixTimeConverterPage/components/InfoContent';
import QuickConversionSidebar from './UnixTimeConverterPage/components/QuickConversionSidebar';
import HistorySidebar from './UnixTimeConverterPage/components/HistorySidebar';
import TipsSidebar from './UnixTimeConverterPage/components/TipsSidebar';

// Определение начального состояния формы
export const initialFormData = {
  conversionType: 'unix-to-human', // 'unix-to-human' или 'human-to-unix'
  unixTimestamp: Math.floor(Date.now() / 1000).toString(), // текущий Unix-timestamp в секундах
  humanDate: new Date().toISOString().slice(0, 16), // текущая дата в формате YYYY-MM-DDThh:mm
  timezone: 'Europe/Moscow', // по умолчанию московское время
  includeMilliseconds: false, // учитывать миллисекунды в Unix-timestamp
  showRFC: true, // показать RFC 2822 формат
  showISO: true, // показать ISO 8601 формат
  showLocale: true, // показать локализованный формат
  showRelative: true, // показать относительное время
};

// Список поддерживаемых часовых поясов России
export const russianTimezones = [
  { id: 'Europe/Kaliningrad', name: 'Калининград (UTC+2)', offset: '+02:00' },
  { id: 'Europe/Moscow', name: 'Москва (UTC+3)', offset: '+03:00' },
  { id: 'Europe/Samara', name: 'Самара (UTC+4)', offset: '+04:00' },
  { id: 'Asia/Yekaterinburg', name: 'Екатеринбург (UTC+5)', offset: '+05:00' },
  { id: 'Asia/Omsk', name: 'Омск (UTC+6)', offset: '+06:00' },
  { id: 'Asia/Novosibirsk', name: 'Новосибирск (UTC+7)', offset: '+07:00' },
  { id: 'Asia/Irkutsk', name: 'Иркутск (UTC+8)', offset: '+08:00' },
  { id: 'Asia/Yakutsk', name: 'Якутск (UTC+9)', offset: '+09:00' },
  { id: 'Asia/Vladivostok', name: 'Владивосток (UTC+10)', offset: '+10:00' },
  { id: 'Asia/Magadan', name: 'Магадан (UTC+11)', offset: '+11:00' },
  { id: 'Asia/Kamchatka', name: 'Камчатка (UTC+12)', offset: '+12:00' }
];

// Список важных исторических дат в Unix-времени для России
export const historicalDates = [
  { 
    timestamp: 662688000, 
    description: 'Запуск Первого канала российского телевидения (1.4.1991)',
    category: 'media' 
  },
  { 
    timestamp: 725846400, 
    description: 'Распад СССР (26.12.1991)',
    category: 'politics' 
  },
  { 
    timestamp: 904694400, 
    description: 'Принятие Конституции РФ (12.12.1998)',
    category: 'politics' 
  },
  { 
    timestamp: 1014854400, 
    description: 'Введение рубля нового образца (1.1.2002)',
    category: 'economy' 
  },
  { 
    timestamp: 1204329600, 
    description: 'Избрание Д.А. Медведева президентом (2.3.2008)',
    category: 'politics'
  },
  { 
    timestamp: 1298937600, 
    description: 'Официальный запуск цифрового ТВ в России (1.3.2011)',
    category: 'technology' 
  },
  { 
    timestamp: 1393632000, 
    description: 'Начало Олимпийских игр в Сочи (1.3.2014)',
    category: 'sports' 
  },
  { 
    timestamp: 1585699200, 
    description: 'Начало пандемии COVID-19 в России (1.4.2020)',
    category: 'health' 
  }
];

/**
 * UnixTimeConverterPage Component - Полный конвертер Unix-времени с различными форматами и дополнительными функциями.
 */
const UnixTimeConverterPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('unix-time-converter'), []);
  const pageTitle = tool?.metaTitle || 'Конвертер Unix-времени | Gensio';
  const metaDescription = tool?.metaDescription || 'Конвертер Unix-времени (Unix timestamp) в человекочитаемый формат даты и времени и обратно. Преобразование Unix timestamp в различные форматы даты с учетом часовых поясов.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [conversionHistory, setConversionHistory] = useState([]);
  const [errors, setErrors] = useState({});
  const [isConverting, setIsConverting] = useState(false);
  const [currentTime, setCurrentTime] = useState(Date.now());

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка истории конвертаций из localStorage ---
  useEffect(() => {
    const savedHistory = localStorage.getItem('unixTimeConversionHistory');
    if (savedHistory) {
      try {
        setConversionHistory(JSON.parse(savedHistory));
      } catch (e) {
        console.error("Ошибка загрузки истории конвертаций:", e);
      }
    }
  }, []);

  // --- Обновление текущего времени каждую секунду ---
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'unix-to-date':
          newState.conversionType = 'unix-to-human';
          break;
        case 'date-to-unix':
          newState.conversionType = 'human-to-unix';
          newState.humanDate = new Date().toISOString().slice(0, 16);
          break;
        case 'milliseconds':
          newState.includeMilliseconds = true;
          break;
        case 'current':
          newState.unixTimestamp = Math.floor(Date.now() / 1000).toString();
          newState.conversionType = 'unix-to-human';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setErrors({});

  }, [subToolId]);

  // Сохранение конвертации в историю
  const saveToHistory = (convertedResult) => {
    const newHistoryItem = {
      id: Date.now().toString(),
      timestamp: convertedResult.unixTimestamp,
      date: convertedResult.humanDateISO,
      timezone: formData.timezone,
      conversionType: formData.conversionType,
      date: new Date().toISOString()
    };

    const updatedHistory = [newHistoryItem, ...conversionHistory].slice(0, 20); // Хранить только последние 20 конвертаций
    setConversionHistory(updatedHistory);
    localStorage.setItem('unixTimeConversionHistory', JSON.stringify(updatedHistory));

    return newHistoryItem;
  };

  // Удаление записи из истории
  const deleteHistoryItem = (id) => {
    const updatedHistory = conversionHistory.filter(item => item.id !== id);
    setConversionHistory(updatedHistory);
    localStorage.setItem('unixTimeConversionHistory', JSON.stringify(updatedHistory));
  };

  // Очистка всей истории
  const clearHistory = () => {
    setConversionHistory([]);
    localStorage.removeItem('unixTimeConversionHistory');
  };

  // Загрузка конвертации из истории
  const loadFromHistory = (id) => {
    const itemToLoad = conversionHistory.find(item => item.id === id);
    if (itemToLoad) {
      setFormData(prev => ({
        ...prev,
        conversionType: itemToLoad.conversionType,
        unixTimestamp: itemToLoad.timestamp.toString(),
        timezone: itemToLoad.timezone || 'Europe/Moscow'
      }));
      
      // Автоматически выполняем конвертацию загруженных данных
      handleConvert({
        conversionType: itemToLoad.conversionType,
        unixTimestamp: itemToLoad.timestamp.toString(),
        timezone: itemToLoad.timezone || 'Europe/Moscow'
      });
    }
  };
  
  // Функция конвертации
  const handleConvert = (data = formData) => {
    setIsConverting(true);
    setErrors({});
    
    try {
      let result = {};
      
      if (data.conversionType === 'unix-to-human') {
        // Конвертация Unix-времени в человекочитаемую дату
        const timestamp = data.includeMilliseconds 
          ? parseInt(data.unixTimestamp, 10)
          : parseInt(data.unixTimestamp, 10) * 1000;
          
        if (isNaN(timestamp)) {
          throw new Error('Некорректный формат Unix-времени');
        }
        
        const date = new Date(timestamp);
        if (isNaN(date.getTime())) {
          throw new Error('Некорректная дата');
        }
        
        // Получаем строковые представления даты в разных форматах
        const options = { 
          timeZone: data.timezone,
          day: '2-digit', 
          month: '2-digit', 
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        };
        
        // ISO 8601 формат
        const isoString = date.toISOString();
        
        // Локализованный формат
        const localeString = date.toLocaleString('ru-RU', options);
        
        // RFC 2822 формат
        const rfc2822String = date.toUTCString();
        
        // Относительное время
        const now = new Date();
        const diffMs = now - date;
        const diffSeconds = Math.floor(diffMs / 1000);
        const diffMinutes = Math.floor(diffSeconds / 60);
        const diffHours = Math.floor(diffMinutes / 60);
        const diffDays = Math.floor(diffHours / 24);
        
        let relativeTimeString = '';
        if (diffSeconds < 0) {
          relativeTimeString = 'В будущем';
        } else if (diffSeconds < 60) {
          relativeTimeString = 'Только что';
        } else if (diffMinutes < 60) {
          relativeTimeString = `${diffMinutes} ${pluralize(diffMinutes, 'минуту', 'минуты', 'минут')} назад`;
        } else if (diffHours < 24) {
          relativeTimeString = `${diffHours} ${pluralize(diffHours, 'час', 'часа', 'часов')} назад`;
        } else {
          relativeTimeString = `${diffDays} ${pluralize(diffDays, 'день', 'дня', 'дней')} назад`;
        }
        
        result = {
          unixTimestamp: data.includeMilliseconds ? data.unixTimestamp : data.unixTimestamp * 1000,
          humanDate: date,
          humanDateISO: isoString,
          humanDateLocale: localeString,
          humanDateRFC: rfc2822String,
          relativeTime: relativeTimeString,
          timezone: data.timezone
        };
        
      } else {
        // Конвертация человекочитаемой даты в Unix-время
        let humanDate = data.humanDate;
        
        if (!humanDate) {
          throw new Error('Пожалуйста, укажите дату и время');
        }
        
        // Создаем объект Date в выбранном часовом поясе
        const dateObj = new Date(humanDate);
        if (isNaN(dateObj.getTime())) {
          throw new Error('Некорректная дата');
        }
        
        // Получаем Unix-время в секундах или миллисекундах
        const unixTimestamp = data.includeMilliseconds 
          ? dateObj.getTime()
          : Math.floor(dateObj.getTime() / 1000);
        
        result = {
          unixTimestamp: unixTimestamp,
          humanDate: dateObj,
          humanDateISO: dateObj.toISOString(),
          humanDateLocale: dateObj.toLocaleString('ru-RU', {
            timeZone: data.timezone,
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
          }),
          humanDateRFC: dateObj.toUTCString(),
          relativeTime: 'Только что',
          timezone: data.timezone
        };
      }
      
      setResults(result);
      
      // Сохраняем в историю
      saveToHistory(result);
      
    } catch (error) {
      console.error("Ошибка конвертации:", error);
      setErrors({ general: error.message || 'Произошла ошибка при конвертации времени' });
      setResults(null);
    } finally {
      setIsConverting(false);
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Clock className="w-8 h-8 text-cyan-600 dark:text-cyan-400 flex-shrink-0" />
                {tool?.name || 'Конвертер Unix-времени'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма конвертера --- */}
            <ConverterForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isConverting={isConverting}
              handleConvert={handleConvert}
              russianTimezones={russianTimezones}
              currentTime={currentTime}
            />

            {/* --- Результаты конвертации --- */}
            {results && !isConverting && (
              <ResultsDisplay results={results} formData={formData} />
            )}
            
            {/* --- Таблица истории конвертаций --- */}
            {conversionHistory.length > 0 && (
              <HistoryTable
                historyItems={conversionHistory.slice(0, 5)}
                loadFromHistory={loadFromHistory}
                deleteHistoryItem={deleteHistoryItem}
                showAllHistory={() => {}}
              />
            )}

            {/* --- Информационный контент о Unix-времени --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой конвертации --- */}
            <QuickConversionSidebar 
              formData={formData}
              setFormData={setFormData}
              handleConvert={handleConvert}
              russianTimezones={russianTimezones}
              historicalDates={historicalDates}
              currentTime={currentTime}
            />

            {/* --- Панель истории конвертаций --- */}
            <HistorySidebar 
              historyItems={conversionHistory}
              loadFromHistory={loadFromHistory}
              deleteHistoryItem={deleteHistoryItem}
              clearHistory={clearHistory}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-cyan-600 focus:ring-cyan-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-cyan-600 focus:ring-cyan-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar for History Table */
        .history-table {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .history-table {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .history-table::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .history-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .history-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .history-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .history-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .history-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .history-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

// Вспомогательная функция для склонения слов
const pluralize = (number, one, two, five) => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return five;
  }
  n %= 10;
  if (n === 1) {
    return one;
  }
  if (n >= 2 && n <= 4) {
    return two;
  }
  return five;
};

export default UnixTimeConverterPage;